import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import {
  ArrowLeft, CheckCircle2, Lock, ShoppingBag, Sparkles, ChevronRight, RotateCw, Layers,
  Cpu, TrendingUp, Compass, Stethoscope, GraduationCap, Megaphone,
} from "lucide-react";

const ICONS = { Cpu, TrendingUp, Compass, Stethoscope, GraduationCap, Megaphone };

function formatPrice(cents, currency) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase() }).format(cents / 100);
}

export default function InterviewPackDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pack, setPack] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [ownedSlugs, setOwnedSlugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [owned, setOwned] = useState(false);
  const [buying, setBuying] = useState(false);
  const [buyingBundle, setBuyingBundle] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const purchase = searchParams.get("purchase");
    if (purchase === "success") {
      toast.success("Pack unlocked — start a mock interview to use it!");
      setSearchParams({}, { replace: true });
    } else if (purchase === "cancel") {
      toast.info("Purchase cancelled.");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: packData }, { data: bundleData }] = await Promise.all([
          api.get(`/packs/${slug}`),
          api.get(`/packs/bundle`),
        ]);
        setPack(packData);
        setBundle(bundleData);
      } catch (err) {
        if (err?.response?.status === 404) {
          toast.error("Pack not found.");
          navigate("/interview/packs", { replace: true });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/packs/me/owned");
        const slugs = data.owned_slugs || [];
        setOwnedSlugs(slugs);
        setOwned(slugs.includes(slug));
      } catch (e) { /* noop */ }
    })();
  }, [user, slug]);

  const handleBuy = async () => {
    if (!user) {
      toast.error("Please sign in first to purchase.");
      window.location.href = `/login?next=/interview/packs/${slug}`;
      return;
    }
    setBuying(true);
    try {
      const { data } = await api.post(`/packs/${slug}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed.");
      setBuying(false);
    }
  };

  const handleBuyBundle = async () => {
    if (!user) {
      toast.error("Please sign in first to buy the bundle.");
      window.location.href = `/login?next=/interview/packs/${slug}`;
      return;
    }
    setBuyingBundle(true);
    try {
      const { data } = await api.post(`/packs/bundle/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bundle checkout failed.");
      setBuyingBundle(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-blue-50/40 via-fuchsia-50/30 to-amber-50/40">
        <div className="max-w-5xl mx-auto px-6 py-20 text-center text-zinc-500">Loading…</div>
      </main>
    );
  }
  if (!pack) return null;

  const Icon = ICONS[pack.icon] || ShoppingBag;

  return (
    <main className="min-h-screen bg-gradient-to-br from-blue-50/40 via-fuchsia-50/30 to-amber-50/40" data-testid={`pack-detail-${pack.slug}`}>
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <Link to="/interview/packs" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900 mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to all packs
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="inline-flex items-center justify-center w-10 h-10" style={{ background: pack.color, color: "white" }}>
                  <Icon className="w-5 h-5" strokeWidth={2.25} />
                </span>
                <span className="font-mono uppercase tracking-wider text-[11px] text-zinc-500">Booster Pack</span>
              </div>
              <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight leading-[1.02] text-zinc-900">
                {pack.name}
              </h1>
              <p className="mt-4 text-lg text-zinc-700 leading-relaxed">{pack.tagline}</p>
            </div>

            <div className="bg-white border border-zinc-200 p-6">
              <h2 className="font-display font-bold text-xl text-zinc-900 mb-3">What&apos;s inside</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">{pack.description}</p>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-zinc-100">
                <div>
                  <div className="font-display font-extrabold text-2xl text-zinc-900">{pack.n_questions}</div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Hand-curated Qs</div>
                </div>
                <div>
                  <div className="font-display font-extrabold text-2xl text-zinc-900">{pack.categories.length}</div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Categories</div>
                </div>
                <div>
                  <div className="font-display font-extrabold text-2xl text-zinc-900">∞</div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Mock reruns</div>
                </div>
                <div>
                  <div className="font-display font-extrabold text-2xl text-zinc-900">{formatPrice(pack.price_cents, pack.currency)}</div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">One-time</div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-zinc-200 p-6">
              <h2 className="font-display font-bold text-xl text-zinc-900 mb-3">Free preview — 2 sample questions</h2>
              <ol className="space-y-4">
                {(pack.preview_questions || []).map((q, i) => (
                  <li key={i} data-testid={`pack-preview-q-${i}`} className="border-l-2 border-zinc-900 pl-4">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-zinc-100 text-zinc-700 border border-zinc-200">{q.category}</span>
                      <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-zinc-900 text-white">{q.difficulty}</span>
                    </div>
                    <p className="font-display font-semibold text-zinc-900">{q.question}</p>
                  </li>
                ))}
              </ol>
              <p className="mt-5 text-xs text-zinc-500 flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> Unlock the remaining {pack.n_questions - 2} questions + all model answers + tips by purchasing.
              </p>
            </div>
          </div>

          {/* Sidebar — CTA */}
          <aside className="lg:col-span-4 space-y-4">
            <div className="bg-white border border-zinc-200 p-6 lg:sticky lg:top-6">
              <div className="text-center mb-4">
                <div className="font-display font-extrabold text-4xl text-zinc-900">
                  {formatPrice(pack.price_cents, pack.currency)}
                </div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mt-1">One-time · Own forever</div>
              </div>
              {owned ? (
                <Link
                  to={`/interview/mock?pack=${pack.slug}`}
                  data-testid="pack-use-cta"
                  className="block text-center px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors"
                >
                  <CheckCircle2 className="inline w-4 h-4 mr-1.5 mb-0.5" />
                  Owned · Use in a mock <ChevronRight className="inline w-4 h-4 ml-1" />
                </Link>
              ) : (
                <button
                  onClick={handleBuy}
                  disabled={buying}
                  data-testid="pack-buy-cta"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
                  style={{ background: pack.color }}
                >
                  {buying ? <RotateCw className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                  {buying ? "Opening checkout…" : `Buy ${pack.name}`}
                </button>
              )}
              <ul className="mt-5 space-y-2 text-xs text-zinc-700">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Unlimited use across mocks</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Model answers + 2-3 tactical tips per Q</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Secure Stripe checkout · Cards / Apple / Google Pay</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Pairs with Premium voice answers</li>
              </ul>
              <p className="mt-5 pt-5 border-t border-zinc-100 text-[11px] text-zinc-500 text-center">
                No subscription required · Pay once, own forever.
              </p>
            </div>

            {/* Bundle upsell — hide if user already owns all 6 */}
            {bundle && bundle.pack_slugs && !bundle.pack_slugs.every((s) => ownedSlugs.includes(s)) ? (
              <div
                data-testid="pack-detail-bundle-callout"
                className="relative overflow-hidden bg-gradient-to-br from-zinc-900 via-indigo-950 to-fuchsia-900 text-white p-5"
              >
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-fuchsia-500/20 rounded-full blur-2xl" />
                <div className="relative">
                  <div className="inline-flex items-center gap-1.5 mb-3">
                    <span className="inline-flex items-center justify-center w-5 h-5 bg-gradient-to-br from-amber-400 to-orange-500 text-zinc-900">
                      <Layers className="w-3 h-3" strokeWidth={2.5} />
                    </span>
                    <span className="font-mono uppercase tracking-widest text-[10px] text-amber-300">
                      Save {formatPrice(bundle.savings_cents, bundle.currency)}
                    </span>
                  </div>
                  <h3 className="font-display font-extrabold text-xl leading-tight mb-2">
                    Get all 6 packs instead
                  </h3>
                  <p className="text-white/80 text-xs leading-relaxed mb-4">
                    Buying this pack alone: <span className="font-semibold text-white">{formatPrice(pack.price_cents, pack.currency)}</span>.
                    Get all 6 (Pro Edition, 150 Qs) for <span className="font-semibold text-white">{formatPrice(bundle.price_cents, bundle.currency)}</span> —
                    <span className="line-through text-white/50 ml-1">{formatPrice(bundle.individual_total_cents, bundle.currency)}</span>.
                    That&apos;s <span className="font-semibold text-amber-300">{bundle.discount_percent}% off</span>.
                  </p>
                  <button
                    onClick={handleBuyBundle}
                    disabled={buyingBundle}
                    data-testid="pack-detail-bundle-buy"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-fuchsia-600 via-indigo-600 to-blue-600 hover:opacity-95 text-white text-xs font-medium disabled:opacity-50 transition-opacity"
                  >
                    {buyingBundle ? <RotateCw className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />}
                    {buyingBundle ? "Opening bundle checkout…" : `Get bundle · ${formatPrice(bundle.price_cents, bundle.currency)}`}
                  </button>
                  <Link
                    to="/interview/packs"
                    className="block mt-2 text-center text-[11px] font-mono uppercase tracking-wider text-white/60 hover:text-white/90 transition-colors"
                  >
                    See what&apos;s in the bundle →
                  </Link>
                </div>
              </div>
            ) : null}
          </aside>
        </div>
      </div>
      <Footer />
    </main>
  );
}
